import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { getActiveTenantId } from '@/lib/tenant';
import type {
  Agent,
  AgentEmailSettings,
  CreateAgentInput,
  InboundEmailListResponse,
  UpdateAgentEmailInput,
  UpdateAgentInput,
} from '@/lib/api/types';

const agentKeys = {
  all: () => ['agents', getActiveTenantId()] as const,
  // The detail key is by `ref` -- a slug or a UUID (spec 0013, AC-9).
  detail: (ref: string) => ['agents', getActiveTenantId(), ref] as const,
};

export function useAgents() {
  return useQuery({
    queryKey: agentKeys.all(),
    queryFn: () => apiFetch<Agent[]>('/agents'),
  });
}

/** Resolve an agent by `ref` -- its slug or UUID (spec 0013, ADR-0002). */
export function useAgent(ref: string | undefined) {
  return useQuery({
    queryKey: ref ? agentKeys.detail(ref) : ['agents', 'detail', 'none'],
    queryFn: () => apiFetch<Agent>(`/agents/${ref}`),
    enabled: Boolean(ref),
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgentInput) =>
      apiFetch<Agent>('/agents', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

export function useUpdateAgent(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAgentInput) =>
      apiFetch<Agent>(`/agents/${id}`, { method: 'PATCH', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

// ----- Agent email (spec 0007, AC-8) -----

const agentEmailKeys = {
  settings: (agentId: string | undefined) =>
    ['agent-email', getActiveTenantId(), agentId] as const,
  messages: (agentId: string | undefined, limit: number, offset: number) =>
    ['agent-email', getActiveTenantId(), agentId, 'messages', limit, offset] as const,
};

export function useAgentEmail(agentId: string | undefined) {
  return useQuery({
    queryKey: agentEmailKeys.settings(agentId),
    queryFn: () => apiFetch<AgentEmailSettings>(`/agents/${agentId}/email`),
    enabled: Boolean(agentId),
  });
}

export function useUpdateAgentEmail(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAgentEmailInput) =>
      apiFetch<AgentEmailSettings>(`/agents/${agentId}/email`, { method: 'PATCH', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agent-email'] });
    },
  });
}

export function useAgentEmailMessages(
  agentId: string | undefined,
  params: { limit?: number; offset?: number } = {},
) {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  return useQuery({
    queryKey: agentEmailKeys.messages(agentId, limit, offset),
    queryFn: () =>
      apiFetch<InboundEmailListResponse>(
        `/agents/${agentId}/email/messages?limit=${limit}&offset=${offset}`,
      ),
    enabled: Boolean(agentId),
  });
}
